import { CenteredLoading } from '@repo/ui/components'
import { DriverHeader } from '@/components/DriverLayout/DriverHeader'
import { isEmbedded } from '@/lib/embedded'
import { TermsAcceptanceGate } from '@/components/TermsAcceptanceGate'
import { useAuthUser } from '@/hooks/queries/auth/useAuthUser'
import { handleInvalidSession } from '@/hooks/queries/auth/useVerifySession'
import { getApi } from '@/stores/api'
import { setPermissions } from '@/stores/permissions'
import { getSessionToken } from '@/stores/session'
import { MILLIS } from '@repo/constants'
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { Toaster } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/_auth')({
  component: DriverLayoutComponent,
  beforeLoad: async ({ context, location }) => {
    const redirectToLogin = () => {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }

    const sessionToken = getSessionToken()
    if (!sessionToken) return redirectToLogin()

    try {
      const meData = await context.queryClient.fetchQuery({
        queryKey: ['me'],
        queryFn: async () => {
          const api = getApi()
          const res = await api.users.me.$post()
          if (!res.ok) throw new Error('Failed to fetch user')
          return res.json()
        },
        staleTime: MILLIS.SECOND * 30,
      })

      if (meData?.permissions) {
        setPermissions(meData.permissions)
      }
    } catch (err) {
      handleInvalidSession(err)
    }
  },
})

const errorStatusSchema = z.object({ status: z.number() })

function ErrorComponent(props: ErrorComponentProps) {
  const navigate = useNavigate()
  const status = errorStatusSchema.safeParse(props.error).data?.status

  if (status === 401) {
    handleInvalidSession(props.error)
    return null
  } else if (status === 403) {
    navigate({ to: '/unauthorized' })
  }

  return <Outlet />
}

function DriverLayoutComponent() {
  const { data: user, error } = useAuthUser()
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    if (error) handleInvalidSession(error)
  }, [error])

  if (!user) return <CenteredLoading fullScreen />

  // Versioned terms-of-service gate (PET-733). Rendered in place of the app —
  // not a route — so it can't be navigated around and needs no redirect loop.
  const pendingTermsVersion = user.terms?.accepted === false ? user.terms.currentVersion : null
  if (pendingTermsVersion) {
    return <TermsAcceptanceGate currentVersion={pendingTermsVersion} />
  }

  return (
    <div className="min-h-svh text-sm text-foreground subpixel-antialiased">
      {/* In the driver app the app draws its own header, sign-out included (`OO-45`). */}
      {!isEmbedded() && <DriverHeader />}
      <main className="mx-auto w-full max-w-lg px-4 py-6">
        <CatchBoundary getResetKey={() => 'reset'} errorComponent={ErrorComponent}>
          <Outlet />
        </CatchBoundary>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  )
}
